import type { ImageObject, VideoObject } from "@sphtech/web2-seo-schema";
import { USFArticleSchema } from "@sphtech/web2-seo-schema";
import {
  ORG_LOGO,
  PUBLISHER_LOGO,
  SITE_NAME,
  SITE_URL,
} from "@src/app/constants/siteIdentity";
import type { BFFArticleDetail } from "@src/app/types/bff";
import { ArticleElementTypeEnum } from "@src/app/types/caas";

type BodyElement = BFFArticleDetail["bodyElements"][number];

/**
 * Fallback image when the article has neither a main media image nor an OG image.
 * Must be an absolute, crawlable URL for Google to follow.
 */
const DEFAULT_META_IMAGE_URL = ORG_LOGO.url;

/**
 * CUE timestamps are UTC ISO 8601 (e.g. "2025-10-03T03:16:23.000Z"), but
 * `edited` is frequently an empty string. Returns `undefined` for empty or
 * unparseable values so callers can fall back to another date.
 */
const toIsoString = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

/** Extracts the YouTube video id from watch / youtu.be / embed / shorts URLs. */
const toYoutubeId = (url: string): string => {
  if (!url) {
    return "";
  }
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return parsed.pathname.split("/")[1] || "";
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (parsed.pathname === "/watch") {
        return parsed.searchParams.get("v") || "";
      }
      const match = parsed.pathname.match(/^\/(?:embed|v|shorts)\/([^/?]+)/);
      return match?.[1] || "";
    }
  } catch {
    return "";
  }
  return "";
};

/** Public embed URL for a YouTube video id. */
const youtubeEmbedUrl = (youtubeId: string) =>
  `https://www.youtube.com/embed/${youtubeId}`;

/** `hqdefault.jpg` is the highest resolution thumbnail YouTube always generates. */
const youtubeThumbnailUrl = (youtubeId: string) =>
  `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;

type VideoElement = {
  youtubeId: string;
  embedUrl: string;
  title?: string;
  description?: string;
  poster?: string;
};

/**
 * ThinkChina embeds videos in the body as `embed` elements (youtu.be links),
 * as a standalone `youtube_video` element (rendered by `ArticleYoutube`), or
 * wrapped in a `media` group. Walks the body elements (including nested
 * lists/media) to find the first YouTube video.
 */
const findYoutubeInBody = (
  elements: BodyElement[],
): VideoElement | undefined => {
  for (const element of elements) {
    if (element.type === ArticleElementTypeEnum.EMBED) {
      const youtubeId = toYoutubeId(element.url);
      if (youtubeId) {
        return {
          youtubeId,
          embedUrl: youtubeEmbedUrl(youtubeId),
        };
      }
    }

    if (element.type === ArticleElementTypeEnum.YOUTUBE_VIDEO) {
      const youtubeId = element.youtubeId || toYoutubeId(element.url);
      if (youtubeId) {
        return {
          youtubeId,
          embedUrl: element.url || youtubeEmbedUrl(youtubeId),
          title: element.title,
          description: element.description,
          poster: element.poster,
        };
      }
    }

    if (element.type === ArticleElementTypeEnum.MEDIA) {
      const media = element.elements.find(
        (i) => i.type === ArticleElementTypeEnum.YOUTUBE_VIDEO,
      );
      if (media) {
        return {
          youtubeId: media.youtubeId,
          embedUrl: media.url,
          title: media.title,
          description: media.description,
          poster: media.poster,
        };
      }
    }

    if (
      element.type === ArticleElementTypeEnum.LIST_BULLETED ||
      element.type === ArticleElementTypeEnum.LIST_NUMBERED
    ) {
      const nested = findYoutubeInBody(element.children);
      if (nested) {
        return nested;
      }
    }
  }

  return undefined;
};

/**
 * Builds the VideoObject used as `NewsArticle.video`.
 *
 * THIN-1570 keeps video inside the article schema (no standalone VideoObject
 * component/route), regardless of the article's displayType.
 *
 * Returns `undefined` when there is not enough data to satisfy Google's
 * required `thumbnailUrl` + `uploadDate`, so the article schema stays valid.
 * It also returns `undefined` when no video could be identified at all — the
 * OG-image fallback below must only apply once a real video is found, otherwise
 * the article would gain a `VideoObject` with no playback URL.
 */
export function buildVideoObject(
  article: BFFArticleDetail,
): VideoObject | undefined {
  const mainVideo = article.mainMedia.find(
    (i) => i.type === ArticleElementTypeEnum.YOUTUBE_VIDEO,
  );
  const bodyVideo = findYoutubeInBody(article.bodyElements);

  // A `VideoObject` must describe an actual video. Bail out before the
  // thumbnail fallbacks below, which would otherwise turn a plain OG image into
  // a video object for an article that has no video at all.
  if (!mainVideo && !bodyVideo) {
    return undefined;
  }

  const youtubeId = mainVideo?.youtubeId || bodyVideo?.youtubeId || "";
  const embedUrl =
    mainVideo?.url ||
    bodyVideo?.embedUrl ||
    (youtubeId ? youtubeEmbedUrl(youtubeId) : "");

  const thumbnailUrl =
    mainVideo?.poster ||
    (youtubeId ? youtubeThumbnailUrl(youtubeId) : "") ||
    article.ogTags?.image ||
    "";

  const uploadDate =
    toIsoString(article.created) ?? toIsoString(article.updated);

  if (!thumbnailUrl || !uploadDate) {
    return undefined;
  }

  return {
    name: mainVideo?.title || bodyVideo?.title || article.title || SITE_NAME,
    description:
      mainVideo?.description ||
      bodyVideo?.description ||
      article.standfirst ||
      article.teaser ||
      article.title ||
      "",
    thumbnailUrl,
    uploadDate,
    ...(embedUrl ? { embedUrl } : {}),
  };
}

/**
 * Maps the ThinkChina article payload to the Webcore USF `NewsArticle` schema.
 *
 * All fields fall back to a safe default so a missing CMS value never breaks
 * page rendering (THIN-1570 acceptance criteria: Fallback Logic).
 */
export default function ArticlePageSEO({
  article,
}: {
  article?: BFFArticleDetail;
}) {
  if (!article) {
    return null;
  }

  // --- Headline ---
  const headline = (article.title || "").replace(/\n/g, "");

  // --- Description ---
  // Prefer the SEO-curated metatags.description, fall back to standfirst/teaser.
  const description =
    article.metaTags?.description ||
    article.standfirst ||
    article.teaser ||
    headline;

  // --- Dates (ISO 8601) ---
  const datePublished =
    toIsoString(article.created) ?? toIsoString(article.updated);
  const dateModified = toIsoString(article.updated) ?? datePublished;

  // `url`, `title` and both dates are required by the schema; skip rendering
  // rather than emitting an invalid blob.
  if (!article.urlPath || !headline || !datePublished || !dateModified) {
    return null;
  }

  // --- Images (Google Discover requires width/height) ---
  const image = article.mainMedia.find(
    (i) => i.type === ArticleElementTypeEnum.IMAGE,
  );
  const images: ImageObject[] = image?.url
    ? [
        {
          url: image.url,
          ...(image.width ? { width: image.width } : {}),
          ...(image.height ? { height: image.height } : {}),
        },
      ]
    : [{ url: article.ogTags?.image || DEFAULT_META_IMAGE_URL }];

  // --- Authors (fall back to the publication when CMS returns none) ---
  const validAuthors = article.authors.filter((author) => author.name.trim());
  const authors =
    validAuthors.length > 0
      ? validAuthors.map((author) => ({
          name: author.name,
          ...(author.urlPath ? { url: `${SITE_URL}${author.urlPath}` } : {}),
        }))
      : [{ name: SITE_NAME }];

  const articleSchemaProps = {
    url: `${SITE_URL}${article.urlPath}`,
    title: headline,
    images,
    datePublished,
    dateModified,
    authors,
    publisherName: SITE_NAME,
    publisherLogo: PUBLISHER_LOGO,
    description,
    inLanguage: "en",
  };

  // Articles with embedded videos expose them through `NewsArticle.video`.
  const video = buildVideoObject(article);

  return <USFArticleSchema {...articleSchemaProps} video={video} />;
}
